import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type Axis = 'vertical' | 'horizontal';

/**
 * A marca de "você está aqui" DESLIZA do item anterior para o novo, em vez de
 * apagar de um lado e acender do outro.
 *
 * O fundo colorido sai de dentro de cada `NavLink` e vira **um elemento só**,
 * posicionado por cima do item ativo. Só assim existe algo que possa se mover:
 * enquanto o realce for uma classe em três links diferentes, não há objeto
 * nenhum viajando — há três caixas piscando. É o que faz a navegação parecer
 * um lugar por onde a pessoa anda, e não três telas que se substituem.
 *
 * **`useLayoutEffect`, não `useEffect`.** A medida precisa estar aplicada antes
 * da pintura; com `useEffect` o navegador exibiria um quadro com a marca na
 * posição velha e ela pularia depois — exatamente o defeito que este hook
 * existe para remover.
 *
 * Quem descobre o item ativo é o `aria-current="page"` do link. Ler o DOM em vez
 * de recalcular a rota mantém uma verdade só sobre o que está ativo — se a
 * regra de `isNavItemActive` mudar (`shared/constants/navigation.ts`), a marca continua
 * certa sem ninguém lembrar de atualizar este arquivo.
 */
export function useNavIndicator<T extends HTMLElement>(axis: Axis, resetKey?: unknown) {
  const containerRef = useRef<T>(null);
  const [box, setBox] = useState<{ start: number; size: number } | null>(null);
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const active = containerRef.current?.querySelector('[aria-current="page"]');

    // Toda rota pertence a uma aba desde agosto/2026, inclusive as de detalhe
    // (`isNavItemActive`), então na prática sempre há um item aceso. O ramo
    // continua aqui para a rota que ninguém previu: a marca some em vez de
    // ficar num item errado — mentir sobre onde a pessoa está é pior que não
    // dizer.
    if (!(active instanceof HTMLElement)) {
      setBox(null);
      return;
    }

    setBox(
      axis === 'vertical'
        ? { start: active.offsetTop, size: active.offsetHeight }
        : { start: active.offsetLeft, size: active.offsetWidth },
    );
  }, [axis, pathname, resetKey]);

  const style = box
    ? axis === 'vertical'
      ? { top: box.start, height: box.size }
      : { left: box.start, width: box.size }
    : null;

  return { containerRef, style };
}
